import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api/api";
import { logClientError } from "../api/errorLogger";
import { tenantDisplayName } from "../utils/tenantProfile";

function formatDate(value) {
  if (!value) return "Not scheduled";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function isExpired(item) {
  return !item.purgeAfter || new Date(item.purgeAfter) <= new Date();
}

export default function Trash() {
  const [trash, setTrash] = useState({ tenants: [], documents: [] });
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [workingItem, setWorkingItem] = useState(null);

  const loadTrash = useCallback(async () => {
    setError("");
    setIsLoading(true);
    try {
      setTrash(await apiFetch("/trash"));
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Trash", action: "loadTrash" });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  const eligibleCount = useMemo(() => (
    trash.tenants.filter(isExpired).length + trash.documents.filter(isExpired).length
  ), [trash]);

  const restore = async (type, id) => {
    setError("");
    setSuccess("");
    setWorkingItem(`${type}-${id}`);
    try {
      await apiFetch(`/trash/${type}/${id}/restore`, { method: "POST" });
      setSuccess(`${type === "tenants" ? "Tenant" : "Document"} restored.`);
      await loadTrash();
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Trash", action: "restore", type, id });
    } finally {
      setWorkingItem(null);
    }
  };

  const purgeExpired = async (event) => {
    event.preventDefault();
    if (!window.confirm("Purge all expired items from the application? This cannot be restored from the dashboard.")) return;

    setError("");
    setSuccess("");
    setWorkingItem("purge");
    try {
      const result = await apiFetch("/trash/purge-expired", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      setPassword("");
      const purged = result.purgedDocuments.length + result.purgedTenants.length;
      setSuccess(`Purged ${purged} expired item${purged === 1 ? "" : "s"}.${result.failures.length ? ` ${result.failures.length} item(s) require retry.` : ""}`);
      await loadTrash();
    } catch (err) {
      setError(err.message);
      logClientError(err, { page: "Trash", action: "purgeExpired" });
    } finally {
      setWorkingItem(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Recovery</p>
          <h2>Trash</h2>
        </div>
      </div>

      <p className="admin-page-note">
        Restore items before their deadline. Purging removes application metadata; AWS version history remains governed by the bucket lifecycle policy.
      </p>
      {error && <p className="admin-empty-state admin-error-state">{error}</p>}
      {success && <p className="admin-empty-state admin-success-state">{success}</p>}

      {isLoading ? (
        <p className="admin-empty-state">Loading Trash...</p>
      ) : (
        <>
          <TrashSection title="Archived tenants" empty="No archived tenants." hasItems={trash.tenants.length > 0}>
            {trash.tenants.map((tenant) => (
              <TrashRow
                key={tenant.id}
                title={tenantDisplayName(tenant)}
                detail={`${tenant.email} · ${tenant.documentCount} document${tenant.documentCount === 1 ? "" : "s"}`}
                item={tenant}
                working={workingItem === `tenants-${tenant.id}`}
                onRestore={() => restore("tenants", tenant.id)}
              />
            ))}
          </TrashSection>

          <TrashSection title="Deleted documents" empty="No deleted documents." hasItems={trash.documents.length > 0}>
            {trash.documents.map((document) => (
              <TrashRow
                key={document.id}
                title={document.name}
                detail={`${document.tenantName} · ${document.deletionReason === "TENANT_ARCHIVE" ? "Archived with tenant" : "Deleted individually"}`}
                item={document}
                working={workingItem === `documents-${document.id}`}
                restoreDisabled={document.tenantArchived}
                restoreTitle={document.tenantArchived ? "Restore the tenant first" : undefined}
                onRestore={() => restore("documents", document.id)}
              />
            ))}
          </TrashSection>

          <section className="admin-form-panel admin-purge-panel" aria-labelledby="purge-title">
            <div className="admin-setting-intro">
              <h3 id="purge-title">Purge expired items</h3>
              <p>{eligibleCount} item{eligibleCount === 1 ? " is" : "s are"} currently eligible. Items are never purged automatically.</p>
            </div>
            <form className="admin-form" onSubmit={purgeExpired}>
              <label>
                Confirm Password
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
              </label>
              <div className="admin-form-actions">
                <button className="admin-button admin-button-danger" type="submit" disabled={!eligibleCount || workingItem !== null}>
                  {workingItem === "purge" ? "Purging..." : "Purge Expired Items"}
                </button>
              </div>
            </form>
          </section>
        </>
      )}
    </div>
  );
}

function TrashSection({ title, empty, hasItems, children }) {
  return (
    <section className="admin-section">
      <div className="admin-section-heading"><h3>{title}</h3></div>
      {!hasItems ? <p className="admin-empty-state">{empty}</p> : <div className="admin-trash-list">{children}</div>}
    </section>
  );
}

function TrashRow({ title, detail, item, working, restoreDisabled = false, restoreTitle, onRestore }) {
  const expired = isExpired(item);
  return (
    <article className="admin-trash-row">
      <div className="admin-trash-details">
        <strong>{title}</strong>
        <span>{detail}</span>
        <span>Deleted {formatDate(item.deletedAt)}{item.deletedBy ? ` by ${item.deletedBy}` : ""}</span>
        <span className={expired ? "admin-trash-expired" : ""}>
          {expired ? "Recovery expired" : `Recoverable until ${formatDate(item.purgeAfter)}`}
        </span>
      </div>
      <button
        className="admin-button admin-button-secondary"
        type="button"
        disabled={expired || restoreDisabled || working}
        title={restoreTitle}
        onClick={onRestore}
      >
        {working ? "Restoring..." : "Restore"}
      </button>
    </article>
  );
}
